import { Component, OnInit, ChangeDetectionStrategy, ElementRef, inject, viewChild } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { User } from 'src/app/models/user.model';
import { CompanyProductPhase } from 'src/app/models/company_product_phase.model';
import { UpsertCompanyProductTeamBody } from 'src/app/models/company_product_team.model';
import { ApiService } from 'src/app/services/api.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatChipGrid, MatChipRow, MatChipRemove, MatChipInput } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete, MatOption } from '@angular/material/autocomplete';
import { MatSelect } from '@angular/material/select';
import { MatButton } from '@angular/material/button';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-company-product-team-credit-dialog',
  templateUrl: './company-product-team-credit-dialog.component.html',
  styleUrl: './company-product-team-credit-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatError,
    MatInput,
    MatChipGrid,
    MatChipRow,
    MatChipRemove,
    MatIcon,
    MatChipInput,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    MatSelect,
    MatDialogActions,
    MatButton,
    AsyncPipe,
  ],
})
export class CompanyProductTeamCreditDialogComponent implements OnInit {
  data = inject(MAT_DIALOG_DATA);
  dialogRef = inject<MatDialogRef<CompanyProductTeamCreditDialogComponent>>(MatDialogRef);
  protected fb = inject(UntypedFormBuilder);
  private api = inject(ApiService);

  form: UntypedFormGroup;

  users: User[] = [];
  usersSearchCtrl = new FormControl('');
  filteredUsers: Observable<User[]>;
  company_product_phases: CompanyProductPhaseOption[] = [];
  private readonly users_input = viewChild<ElementRef<HTMLInputElement>>('users_input');

  get isCreate() {
    return this.data.company_product_team.id == null;
  }

  constructor() {
    this.form = this.fb.group({
      name: ['', Validators.compose([Validators.required, Validators.minLength(5)])],
      company_product_id: ['', Validators.required],
      users: [[], Validators.compose([Validators.required, Validators.minLength(1)])],
      company_product_phase_ids: [[]],
    });

    this.initFilteredUsers();
  }

  ngOnInit(): void {
    this.patchInput();
    this.fetchUsers();
    this.fetchCompanyProductPhases();
  }

  removeUser(idx: number) {
    const users: User[] = this.form.value.users;
    users.splice(idx, 1);
    this.form.controls['users'].patchValue(users);
  }

  selectedUser(event: MatAutocompleteSelectedEvent) {
    const user = event.option.value as User;
    const users: User[] = [...(this.form.value.users ?? [])];
    if (!users.some(selected => selected.id === user.id)) {
      users.push(user);
      this.form.controls['users'].patchValue(users);
    }
    this.clearUsersSearchInput();
  }

  submit() {
    const data: UpsertCompanyProductTeamBody = {
      ...this.form.value,
      users: this.form.value.users.map((u: User) => u.id),
      company_product_phase_ids: this.form.value.company_product_phase_ids ?? [],
    };

    if (this.isCreate) {
      this.api.createCompanyProductTeam(data).subscribe(created => {
        this.dialogRef.close(created);
      });
    } else {
      this.api.updateCompanyProductTeam(this.data.company_product_team.id, data).subscribe(updated => {
        this.dialogRef.close(updated);
      });
    }
  }

  private clearUsersSearchInput(): void {
    this.usersSearchCtrl.setValue('');
    const input = this.users_input()?.nativeElement;
    if (input) {
      input.value = '';
    }
  }

  private initFilteredUsers() {
    this.filteredUsers = this.usersSearchCtrl.valueChanges.pipe(
      startWith(null),
      map((user: string | null) => this._filterUsers(typeof user === 'string' ? user : ''))
    );
  }

  private _filterUsers(user: string): User[] {
    const filterValue = user.trim().toLowerCase();
    const selected_ids = new Set((this.form.value.users ?? []).map((selected: User) => selected.id));
    return this.users.filter(candidate => {
      if (selected_ids.has(candidate.id)) {
        return false;
      }
      if (!filterValue) {
        return true;
      }
      return `${candidate.name} ${candidate.surnames ?? ''}`.toLowerCase().includes(filterValue);
    });
  }

  private patchInput() {
    this.form.patchValue(this.data.company_product_team);
  }

  private fetchUsers() {
    this.api.getActiveCompanyProductMembersWithBusiness(this.form.value.company_product_id).subscribe(data => {
      this.users = data.bisual_users.map(m => m.user).concat(data.company_users);
    });
  }

  private fetchCompanyProductPhases() {
    const team_id = this.data.company_product_team.id;

    this.api
      .getCompanyProductPhases({
        company_product_id: this.form.value.company_product_id,
        is_active: true,
        with: 'company_product_phase_type',
      })
      .subscribe(response => {
        this.company_product_phases = response.data
          .filter(phase => phase.is_active || (team_id != null && phase.company_product_team_id === team_id))
          .map(phase => this.serializePhaseOption(phase));

        this.form.controls['company_product_phase_ids'].patchValue(
          this.company_product_phases.filter(phase => team_id != null && phase.company_product_team_id === team_id).map(phase => phase.id)
        );
      });
  }

  private serializePhaseOption(phase: CompanyProductPhase): CompanyProductPhaseOption {
    return {
      id: phase.id,
      company_product_team_id: phase.company_product_team_id,
      label: phase.company_product_phase_type?.name ?? `Fase #${phase.id}`,
    };
  }
}

interface CompanyProductPhaseOption {
  id: number;
  company_product_team_id: number | null;
  label: string;
}
